import {
  Button, Input, Select, SelectItem, Switch, Textarea,
} from '@heroui/react';
import { ImagePlus } from 'lucide-react';
import React, { FC, useState } from 'react';

import type { EmailAlign, EmailBlock, EmailTheme } from '../model/types';
import { EMAIL_PLACEHOLDERS } from '../model/types';
import { ImageSourceModal } from './ImageSourceModal';

const ALIGNS: EmailAlign[] = ['left', 'center', 'right'];
const ALIGN_LABELS: Record<EmailAlign, string> = { left: 'Do lewej', center: 'Wyśrodkowany', right: 'Do prawej' };

interface InspectorProps {
  block: EmailBlock | null;
  theme: EmailTheme;
  onChange: (patch: Partial<EmailBlock>) => void;
  onThemeChange: (patch: Partial<EmailTheme>) => void;
}

/**
 * Kolor jako para: próbnik systemowy i pole z zapisem szesnastkowym.
 *
 * `Input type="color"` z HeroUI rozjeżdżał panel — komponent rysuje etykietę pływającą nad
 * polem, a natywny próbnik ma własną, sztywną wysokość i wychodził poza obramowanie.
 */
const ColorRow: FC<{
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, hint, value, onChange }) => (
  <div className="flex w-full min-w-0 flex-col gap-1">
    <span className="text-xs font-medium text-gray-700">{label}</span>
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-gray-200 bg-white p-1"
      />
      <Input
        size="sm"
        aria-label={`${label}, zapis szesnastkowy`}
        value={value}
        className="min-w-0 flex-1"
        onValueChange={onChange}
      />
    </div>
    <span className="text-[11px] leading-tight text-gray-400">{hint}</span>
  </div>
);

const AlignField: FC<{ value: EmailAlign; onChange: (value: EmailAlign) => void }> = ({ value, onChange }) => (
  <Select
    size="sm"
    label="Wyrównanie"
    selectedKeys={[value]}
    onSelectionChange={(keys) => onChange(Array.from(keys)[0] as EmailAlign)}
  >
    {ALIGNS.map((align) => <SelectItem key={align}>{ALIGN_LABELS[align]}</SelectItem>)}
  </Select>
);

/** Ustawienia zaznaczonego bloku. Bez zaznaczenia pokazujemy wygląd całej wiadomości. */
export const Inspector: FC<InspectorProps> = ({ block, theme, onChange, onThemeChange }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!block) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Wygląd wiadomości</p>

        <ColorRow
          label="Kolor akcentu"
          hint="Przyciski i odnośniki."
          value={theme.brand}
          onChange={(brand) => onThemeChange({ brand })}
        />
        <ColorRow
          label="Tło wiadomości"
          hint="Poza treścią, widoczne na szerokim ekranie."
          value={theme.background}
          onChange={(background) => onThemeChange({ background })}
        />

        <Input
          size="sm"
          type="number"
          label="Szerokość treści (px)"
          value={String(theme.width)}
          description="600 px pokazuje bez cięcia każda skrzynka."
          onValueChange={(value) => onThemeChange({ width: Number(value) || 600 })}
        />

        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-700">Pola podstawiane przy wysyłce</p>
          <ul className="mt-2 flex flex-col gap-1">
            {EMAIL_PLACEHOLDERS.map((item) => (
              <li key={item.token} className="text-xs text-gray-500">
                <code className="rounded bg-white px-1 py-0.5 text-gray-800">{item.token}</code>
                {` ${item.label}`}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Ustawienia bloku</p>

      {block.kind === 'logo' && (
        <>
          <Input size="sm" label="Adres pliku" value={block.src} onValueChange={(src) => onChange({ src })} />
          <Button
            size="sm"
            variant="bordered"
            startContent={<ImagePlus className="h-4 w-4" />}
            onPress={() => setPickerOpen(true)}
          >
            Wybierz z biblioteki
          </Button>
          <Input
            size="sm"
            type="number"
            label="Wysokość (px)"
            value={String(block.height)}
            onValueChange={(value) => onChange({ height: Number(value) || 24 })}
          />
          <AlignField value={block.align} onChange={(align) => onChange({ align })} />
        </>
      )}

      {block.kind === 'heading' && (
        <>
          <Textarea size="sm" label="Treść" value={block.text} onValueChange={(text) => onChange({ text })} />
          <Select
            size="sm"
            label="Poziom"
            selectedKeys={[String(block.level)]}
            onSelectionChange={(keys) => onChange({ level: Number(Array.from(keys)[0]) === 1 ? 1 : 2 })}
          >
            <SelectItem key="1">Główny (H1)</SelectItem>
            <SelectItem key="2">Sekcja (H2)</SelectItem>
          </Select>
          <AlignField value={block.align} onChange={(align) => onChange({ align })} />
        </>
      )}

      {block.kind === 'text' && (
        <>
          <Textarea
            size="sm"
            minRows={5}
            label="Treść"
            description="Pusta linia zaczyna nowy akapit."
            value={block.text}
            onValueChange={(text) => onChange({ text })}
          />
          <AlignField value={block.align} onChange={(align) => onChange({ align })} />
          <Switch size="sm" isSelected={block.small} onValueChange={(small) => onChange({ small })}>
            Drobny druk
          </Switch>
        </>
      )}

      {block.kind === 'button' && (
        <>
          <Input size="sm" label="Napis" value={block.label} onValueChange={(label) => onChange({ label })} />
          <Input
            size="sm"
            label="Adres"
            description="https://, mailto: albo pole podstawiane."
            value={block.href}
            onValueChange={(href) => onChange({ href })}
          />
          <AlignField value={block.align} onChange={(align) => onChange({ align })} />
          <Switch size="sm" isSelected={block.fullWidth} onValueChange={(fullWidth) => onChange({ fullWidth })}>
            Na całą szerokość
          </Switch>
        </>
      )}

      {block.kind === 'image' && (
        <>
          {block.src ? (
            <img
              src={block.src}
              alt=""
              className="max-h-36 w-full rounded-lg border border-gray-200 object-contain p-1"
            />
          ) : null}
          <Button
            size="sm"
            variant="bordered"
            startContent={<ImagePlus className="h-4 w-4" />}
            onPress={() => setPickerOpen(true)}
          >
            {block.src ? 'Zmień grafikę' : 'Wgraj albo wybierz z biblioteki'}
          </Button>
          <Input size="sm" label="Adres grafiki" value={block.src} onValueChange={(src) => onChange({ src })} />
          <Input
            size="sm"
            label="Tekst alternatywny"
            description="Skrzynki często blokują grafiki, wtedy zostaje sam ten tekst."
            value={block.alt}
            onValueChange={(alt) => onChange({ alt })}
          />
          <Input
            size="sm"
            label="Odnośnik (opcjonalnie)"
            value={block.href}
            onValueChange={(href) => onChange({ href })}
          />
          <Select
            size="sm"
            label="Zaokrąglenie rogów"
            selectedKeys={[String(block.radius)]}
            description="Outlook pokaże prostokąt, pozostałe klienty zaokrąglenie."
            onSelectionChange={(keys) => onChange({ radius: Number(Array.from(keys)[0]) })}
          >
            <SelectItem key="0">Bez zaokrąglenia</SelectItem>
            <SelectItem key="8">Lekkie (8 px)</SelectItem>
            <SelectItem key="16">Mocne (16 px)</SelectItem>
          </Select>
        </>
      )}

      {block.kind === 'list' && (
        <Textarea
          size="sm"
          minRows={5}
          label="Punkty"
          description="Każdy wiersz to osobny punkt."
          value={block.items.join('\n')}
          onValueChange={(value) => onChange({ items: value.split('\n') })}
        />
      )}

      {block.kind === 'spacer' && (
        <Input
          size="sm"
          type="number"
          label="Wysokość (px)"
          value={String(block.height)}
          onValueChange={(value) => onChange({ height: Number(value) || 0 })}
        />
      )}

      {block.kind === 'footer' && (
        <>
          <Textarea size="sm" label="Treść stopki" value={block.text} onValueChange={(text) => onChange({ text })} />
          <Input
            size="sm"
            label="Adres wypisania"
            description="Puste pole ukrywa odnośnik."
            value={block.unsubscribeUrl}
            onValueChange={(unsubscribeUrl) => onChange({ unsubscribeUrl })}
          />
          <Input
            size="sm"
            label="Napis odnośnika"
            value={block.unsubscribeLabel}
            onValueChange={(unsubscribeLabel) => onChange({ unsubscribeLabel })}
          />
        </>
      )}

      {block.kind === 'divider' && (
        <p className="text-xs text-gray-500">Linia nie ma ustawień. Kolor bierze z wyglądu wiadomości.</p>
      )}

      {pickerOpen && (
        <ImageSourceModal
          onClose={() => setPickerOpen(false)}
          onPick={(url) => {
            onChange({ src: url } as Partial<EmailBlock>);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
};
